import { spanTypes, metaTypes, structureTypes } from './ElementTypes';
import { Sentence } from './TextSegment.d';

const sentenceTerminator = /[.?!]\s/;
const abbreviationLookbackChars = 4;
const openBracketRegex = /\([^)]{0,100}$/;
const closeBracketRegex = /^[^(]{0,100}\)/;
const footnoteRegex = /(^sdfootnote$|\ssdfootnote$|^sdfootnote\s|\ssdfootnote\s)/;

const isBlockType = (tagName: string): boolean => spanTypes.indexOf(tagName) < 0;
const isStructureType = (tagName: string): boolean => structureTypes.indexOf(tagName) >= 0;
const isFootnote = (el: HTMLElement): boolean => (
  (typeof el.className === 'string' && !!el.className.match(footnoteRegex)) ||
  (typeof el.id === 'string' && !!el.id.match(/^sdfootnote\d+$/))
);

const isHeader = (el: HTMLElement): boolean => typeof el.title === 'string' && el.title.toLowerCase() === 'header';

const isFooter = (el: HTMLElement): boolean => typeof el.title === 'string' && el.title.toLowerCase() === 'footer';

// Iterate over the document and return a list of all sentences along with their element start/end node and offset
const sentenceLookup = (rootNode: Element): Sentence[] => {
  const sentences: Sentence[] = [];

  let currentSentence = '';
  let buffer = '';
  let startElement = null;
  let startOffset = 0;
  let endElement = null;
  let endOffset = 0;
  let sentenceIndex = 0;
  let paragraphIndex = -1;
  let paragraphSentenceFound = false;
  let paragraphType = null;
  let structureType = null;
  let paragraphSentenceIndex = 0;
  const paragraphSentenceCounts = {};

  const addSentence = (): void => {
    currentSentence = currentSentence.replace(/\n/g, ' ').trim();
    if (currentSentence === '') return;

    if (!paragraphSentenceFound) {
      paragraphSentenceFound = true;
      paragraphIndex += 1;
      paragraphSentenceIndex = 0;
    }

    const previousSentence: Sentence = sentences[sentences.length - 1];
    // Check for partial "reference-y" sentences
    if (paragraphSentenceIndex > 0 &&
      previousSentence.text.match(openBracketRegex) &&
      currentSentence.match(closeBracketRegex)) {
      previousSentence.text += ` ${currentSentence.replace(/\s+/g, ' ')}`;
      previousSentence.endElement = endElement;
      previousSentence.endOffset = endOffset;
    } else {
      sentences.push({
        text: currentSentence.replace(/\s+/g, ' '),
        startElement,
        startOffset,
        endElement,
        endOffset,
        startIndex: sentenceIndex,
        endIndex: sentenceIndex,
        paragraphIndex,
        paragraphType: structureType || paragraphType,
        paragraphSentenceIndex,
        paragraphSentenceCount: null
      });
      sentenceIndex += 1;
      paragraphSentenceIndex += 1;
      paragraphSentenceCounts[paragraphIndex] = paragraphSentenceIndex;
    }
    currentSentence = '';
    startElement = null;
  };

  const findEndOfSentence = (currentBuffer: string): number => {
    let index = currentBuffer.search(sentenceTerminator);

    // Check to see that the sentence terminator (period) isn't part of an abbreviation
    while (index >= 0 && currentBuffer[index] === '.') {
      const previousIndex = currentBuffer.substr(0, index).split('').reverse().join('').
        search(/\./);
      if (previousIndex >= 0 && previousIndex < abbreviationLookbackChars) {
        const nextIndex = currentBuffer.substr(index + 1, currentBuffer.length).search(sentenceTerminator);
        if (nextIndex >= 0) {
          index += nextIndex + 1;
        } else {
          return -1;
        }
      } else {
        break;
      }
    }
    return index;
  };

  const parseTextElement = (el: Element): void => {
    if (el.textContent.search(/\S/) !== -1) {
      // If we haven't yet assigned a startElement, set it to this element
      if (!startElement) {
        startElement = el;
        startOffset = el.textContent.search(/\S/);
      }
      // Move the endElement pointer to this element
      endElement = el;
      // Grab the element content and put it, along with the previous buffer, in a temporary working buffer
      let currentBuffer = buffer + el.textContent;
      // Assign the current offset to the inverse buffer length (so the currentBuffer offset is effectively 0)
      let currentOffset = -1 * buffer.length;
      // Try find the end of a sentence
      let endOfSentence = findEndOfSentence(currentBuffer);

      if (buffer !== '' && endOfSentence < 0) {
        // If the current buffer is not empty and we didn't find a sentence
        // terminator in this element save the buffer for later
        buffer = currentBuffer;
        endOffset = el.textContent.length;
      } else {
        // Otherwise, loop over the current buffer until all of the sentences have been found
        while (endOfSentence >= 0 && currentBuffer.trim() !== '') {
          // Current sentence is everything in the buffer + the part of
          // the current working buffer up to the end of the sentence
          currentSentence = currentBuffer.substr(0, endOfSentence + 1);
          // The current buffer is then everything from the end of the sentence onwards
          currentBuffer = currentBuffer.substr(endOfSentence + 1, currentBuffer.length);
          // Assign the endOffset into the current element
          endOffset = currentOffset + endOfSentence + 1;
          // Reset the overall buffer
          buffer = '';
          addSentence();

          // If there is something still in the working buffer, reset the working
          // parameters and see if there are any more sentences in the buffer
          if (currentBuffer.trim() !== '') {
            startElement = el;
            currentOffset = endOffset;
            startOffset = currentOffset + currentBuffer.search(/\S/);
            endOfSentence = findEndOfSentence(currentBuffer);
          }
        }

        // Once we can't find any more sentences, save the current working buffer for later
        buffer = currentBuffer;
        endOffset = startOffset + buffer.length - currentBuffer.search(/\S/);
      }
    } else if (el.textContent.length > 0) {
      const endOfSentence = findEndOfSentence(`${buffer} `);
      if (endOfSentence > 0 && endOfSentence === buffer.length - 1) {
        // The sentence is now complete so we can save it and start again
        currentSentence = buffer;
        buffer = '';
        addSentence();
      } else {
        // The node just has whitespace in it. We don't need to go looking for the end of sentence,
        // but we do need to add the (some) whitespace to the buffer
        buffer += ' ';
      }
    }
  };

  const finaliseSentence = (): void => {
    currentSentence = buffer;
    addSentence();
    buffer = '';
  };

  const parseElement = (el: HTMLElement): void => {
    if (el.nodeType === Node.TEXT_NODE) {
      parseTextElement(el);
    } else {
      const tagName = el.nodeName.toLowerCase();
      const blockElement = isBlockType(tagName);

      if (isStructureType(tagName)) structureType = tagName;
      else if (isFootnote(el)) structureType = 'footnote';
      else if (isHeader(el)) structureType = 'header';
      else if (isFooter(el)) structureType = 'footer';

      if (el instanceof Element && tagName === 'span' && el.className.match(/\binline-comment\b/)) {
        // We need to ignore these as they're added for sentence annotations but before the paragraph parsing is done
        return;
      }

      if (el.nodeType === Node.ELEMENT_NODE && metaTypes.indexOf(tagName) < 0) {
        if (blockElement) {
          // Check if there is anything in the buffer before the start of the block
          finaliseSentence();

          // Update the paragraph tag type
          paragraphType = tagName;

          // Reset paragraph sentence found flag
          paragraphSentenceFound = false;
        }

        // Parse all of the child elements
        el.childNodes.forEach((childElement: HTMLElement) => parseElement(childElement));

        if (blockElement) {
          // Reset the paragraph tag type
          paragraphType = tagName;

          // Check if there is anything in the buffer at the end of the block
          finaliseSentence();

          // Reset paragraph sentence found flag
          paragraphSentenceFound = false;
        }

        // Revert the paragraph/structure tag types back to the nearest block type parent tag name
        let node: HTMLElement = el;
        let foundBlockType = false;
        structureType = null;
        while (node !== rootNode) {
          node = node.parentElement;
          const parentTagName = node.nodeName.toLowerCase();

          if (!foundBlockType && isBlockType(parentTagName)) {
            foundBlockType = true;
            paragraphType = parentTagName;
          }

          if (isStructureType(parentTagName)) {
            structureType = parentTagName;
            break;
          } else if (isFootnote(node)) {
            structureType = 'footnote';
            break;
          } else if (isHeader(node)) {
            structureType = 'header';
            break;
          } else if (isFooter(node)) {
            structureType = 'footer';
            break;
          }
        }
      }
    }
  };

  // Kick things off by looping through all of the elements and parsing them
  $(rootNode).contents().each((i, element: HTMLElement) => parseElement(element));

  // Handle the remainder left in the buffer
  const finalOffset = buffer.split('').reverse().join('').search(/\S/);
  if (finalOffset >= 0) endOffset -= finalOffset + 1;
  currentSentence = buffer;
  addSentence();

  // Add the paragraph sentence count
  sentences.forEach((sentence) => {
    // eslint-disable-next-line no-param-reassign
    sentence.paragraphSentenceCount = paragraphSentenceCounts[sentence.paragraphIndex];
  });

  return sentences;
};

export default sentenceLookup;
